import * as React from 'react'
import moment from 'moment'
import { useNavigate, useParams } from 'react-router-dom'
import {
  FaIcon,
  faCalendarRangeSolid,
  faCalendarsSolid,
  faCalendarWeekSolid,
  faRefreshSolid,
  faFerrySolid,
} from '@/lib/fa'
import Tooltip from '@/modules/drivers/shared/ToolTip'
import DividerVertical from '@/modules/drivers/shared/DividerVertical'
import { useActivity } from '@/modules/vehicles/stores/activityStore'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useDate } from '@/modules/drivers/stores/dateStore'
import { format } from 'date-fns'
import { VEHICLE_ROUTES } from '../../routes-typed'
import { Slot } from '@/app/constants/slot'
import VehicleSpeedModal from '@/modules/vehicles/modals/speed/VehicleSpeedModal'
import { ReportType } from '@/app/constants/reportType'
import { OutputType } from '@/app/constants/outputType'
import { TaskType } from '@/app/constants/taskType'
import { getReportNumberByType } from '@/app/services/printout/reportTypeResolver'
import { useTask } from '@/modules/shared/context/TaskContext'
import { PrintActionButton } from '@/modules/printouts/components/PrintActionButton'
import { usePrintPdf } from '@/modules/printouts/hooks/usePrintPdf'
import { usePrintJobGuard } from '@/modules/printouts/hooks/usePrintJobGuard'
import { toast } from '@/lib/toasts/api'

const LEGEND_ITEMS = [
  { color: '#FD5C54', labelKey: 'activityName.driving' },
  { color: '#F5AE31', labelKey: 'activityName.work' },
  { color: '#FFF300', labelKey: 'activityName.availability' },
  { color: '#75C5F0', labelKey: 'activityName.rest' },
  { color: '#C0C0C0', labelKey: 'activityName.unknown' },
]

const VehicleActivitiesActionBar = ({
  printView,
}: {
  printView?: 'daily' | 'weekly'
}) => {
  const { type, slot, setSlot } = useActivity()
  const { t } = useLocalize()
  const { single: singleDate, getDateForView } = useDate()
  const navigate = useNavigate()
  const { printPdf } = usePrintPdf()
  const { addTask } = useTask()
  const { busyTooltip, runPrintJob } = usePrintJobGuard()

  const {
    companyId: cidStr,
    vehicleId: vidStr,
    date,
  } = useParams<{
    companyId: string
    vehicleId: string
    date: string
  }>()
  const companyId = cidStr ?? ''
  const vehicleId = Number(vidStr)
  const today = new Date()
  const currentYearFormatted = format(today, 'YYYY')
  const [isLegendOpen, setIsLegendOpen] = React.useState(false)
  const legendRef = React.useRef<HTMLDivElement>(null)
  const [isSpeedModalOpen, setIsSpeedModalOpen] = React.useState(false)
  const speedModalDate =
    singleDate || getDateForView('daily') || format(today, 'YYYY-MM-DD')

  const printActivities = async () => {
    if (!printView || !date) return

    const reportType =
      printView === 'daily'
        ? ReportType.VEHICLE_DAILY_ACTIVITIES
        : ReportType.VEHICLE_WEEKLY_ACTIVITIES
    const startTime = moment(date, 'YYYY-MM-DD', true).startOf('day')
    const endTime = startTime
      .clone()
      .add(printView === 'weekly' ? 6 : 0, 'days')
      .endOf('day')

    try {
      const { taskId } = await printPdf({
        companyId,
        reportType: getReportNumberByType(reportType),
        drivers: null,
        vehicles: [String(vehicleId)],
        startTime,
        endTime,
        outputType: OutputType.Pdf,
        data:
          printView === 'weekly'
            ? { separateFiles: false, slot }
            : { separateFiles: false },
      })
      addTask(taskId, TaskType.Printout, reportType)
      toast.info({ text: t('printout.generation-started') })
    } catch {
      toast.error({ text: t('printout.generation-error') })
    }
  }

  React.useEffect(() => {
    if (!isLegendOpen) return undefined
    const handleClick = (e: MouseEvent) => {
      if (legendRef.current && !legendRef.current.contains(e.target as Node)) {
        setIsLegendOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [isLegendOpen])

  return (
    <div className="tw-flex tw-w-full tw-justify-between tw-px-4 tw-py-3.5 md:tw-items-center">
      <div className="tw-flex tw-items-end tw-gap-3">
        <Tooltip
          portal
          checked={type === 'daily'}
          text={t('vehicleModule.view-daily')}
          onClick={() =>
            navigate(
              VEHICLE_ROUTES.dailyActivity.buildPath({
                companyId,
                vehicleId,
                date: getDateForView('daily'),
              }),
            )
          }
        >
          <FaIcon icon={faCalendarWeekSolid} className="tw-text-primary" />
        </Tooltip>

        <Tooltip
          portal
          checked={type === 'weekly'}
          text={t('vehicleModule.view-weekly')}
          onClick={() =>
            navigate(
              VEHICLE_ROUTES.weeklyActivity.buildPath({
                companyId,
                vehicleId,
                date: getDateForView('weekly'),
              }),
            )
          }
        >
          <FaIcon icon={faCalendarRangeSolid} className="tw-text-primary" />
        </Tooltip>

        <Tooltip
          portal
          checked={type === 'yearly'}
          text={t('vehicleModule.view-yearly')}
          onClick={() =>
            navigate(
              VEHICLE_ROUTES.calendar.buildPath({
                companyId,
                vehicleId,
                year: Number(currentYearFormatted),
              }),
            )
          }
        >
          <FaIcon icon={faCalendarsSolid} className="tw-text-primary" />
        </Tooltip>

        <DividerVertical />

        {printView && (
          <>
            <PrintActionButton
              tooltip={t('common.print')}
              onClick={() => runPrintJob(printActivities)}
              disabled={
                !companyId ||
                !Number.isFinite(vehicleId) ||
                vehicleId <= 0 ||
                !moment(date, 'YYYY-MM-DD', true).isValid()
              }
              blockedText={busyTooltip}
            />
            <DividerVertical />
          </>
        )}

        {type === 'weekly' && (
          <>
            <Tooltip
              portal
              text={t('menu.slot-driver')}
              onClick={() => setSlot(Slot.Driver)}
            >
              <div
                className={`tw-grid tw-h-5 tw-w-5 tw-place-items-center tw-rounded ${slot === Slot.Driver ? 'tw-bg-primary' : 'tw-bg-primary/30'}`}
              >
                <FaIcon
                  icon={faFerrySolid}
                  className={`tw-h-4 tw-w-4 ${slot === Slot.Driver ? 'tw-text-white' : 'tw-text-content-base'}`}
                />
              </div>
            </Tooltip>
            <Tooltip
              portal
              text={t('menu.slot-codriver')}
              onClick={() => setSlot(Slot.CoDriver)}
            >
              <div
                className={`tw-grid tw-h-5 tw-w-5 tw-place-items-center tw-rounded ${slot === Slot.CoDriver ? 'tw-bg-primary' : 'tw-bg-primary/30'}`}
              >
                <FaIcon
                  icon={faFerrySolid}
                  className={`tw-h-4 tw-w-4 ${slot === Slot.CoDriver ? 'tw-text-white' : 'tw-text-content-base'}`}
                />
              </div>
            </Tooltip>
            <DividerVertical />
          </>
        )}

        {/* <Tooltip text={t('vehicleModule.toggle-violations')} disabled={devViolations}>
          <div
            className={`tw-grid tw-h-5 tw-w-5 tw-place-items-center tw-rounded ${showViolations ? 'tw-bg-primary' : 'tw-bg-primary/30'}`}
          >
            <FaIcon
              icon={faTriangleExclamationRegular}
              className="tw-h-4 tw-w-4 tw-text-white"
            />
          </div>
        </Tooltip> */}

        {/* <Tooltip
          text={t('vehicleModule.toggle-border-crossings')}
          disabled={devViolations}
        >
          <div
            className={`tw-grid tw-h-5 tw-w-5 tw-place-items-center tw-rounded ${showFerryCrossings ? 'tw-bg-primary' : 'tw-bg-primary/30'}`}
          >
            <FaIcon
              icon={faFlagswallowtailRegular}
              className="tw-h-4 tw-w-4 tw-text-white"
            />
          </div>
        </Tooltip> */}

        {/* <DividerVertical /> */}

        <div className="tw-relative" ref={legendRef}>
          <p
            className="tw-m-0 tw-cursor-pointer tw-text-sm tw-font-medium tw-text-primary tw-underline"
            onClick={() => setIsLegendOpen((prev) => !prev)}
          >
            {t('vehicleModule.legend')}
          </p>
          {isLegendOpen && (
            <div className="tw-absolute tw-left-0 tw-top-full tw-z-50 tw-mt-2 tw-min-w-[180px] tw-rounded-lg tw-bg-white tw-p-3 tw-shadow-lg">
              {LEGEND_ITEMS.map(({ color, labelKey }) => (
                <div
                  key={labelKey}
                  className="tw-flex tw-items-center tw-gap-2 tw-py-1"
                >
                  <div
                    className="tw-h-4 tw-w-6 tw-flex-shrink-0 tw-rounded-sm tw-border tw-border-gray-200"
                    style={{ backgroundColor: color }}
                  />
                  <span className="tw-text-sm">{t(labelKey)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <Tooltip
          portal
          className="tw-grid tw-place-items-center"
          text={t('menu.speed-chart') || 'Wykres prędkości'}
          onClick={() => setIsSpeedModalOpen(true)}
        >
          <img
            src="/assets/icons_nucleo/icons/import/dane_gps.svg"
            alt="Speed"
            className="tw-h-4 tw-w-4"
          />
        </Tooltip>
      </div>

      <div className="tw-flex tw-items-end tw-gap-5">
        <Tooltip portal text={t('vehicleModule.refresh')}>
          <FaIcon icon={faRefreshSolid} className="tw-text-primary" />
        </Tooltip>
      </div>
      {isSpeedModalOpen && (
        <VehicleSpeedModal
          companyId={companyId}
          vehicleId={vehicleId}
          date={speedModalDate}
          onClose={() => setIsSpeedModalOpen(false)}
        />
      )}
    </div>
  )
}

export default VehicleActivitiesActionBar
